import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Delete from "../../../../icons/delete.svg";
import { Pencil } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { UsuarioContext } from "../../../../providers/userContext";
import { Box, Tooltip } from "@mui/material";
import { DataTable } from "../../../../components";
import { construirURLParamsFiltros, useRecogerURLParamsFiltros } from "../../../../functions/parametrosURL/funcionesParamsURL";
import { withRouter } from "../../../../providers/withRouter";
import { getProductosKaeAlmacen, deleteStockKae } from "../../../../services";
import Warning from "../../../../icons/warning.svg";
import ModalEditarStockProducto from "../stock/EditarStockProducto";

const Productos = ({idAlmacen, recargar: recargarExterno, setRecargar: setRecargarExterno}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { filtroOrder } = useRecogerURLParamsFiltros();
  const user = useContext(UsuarioContext);
  const [loading, setLoading] = useState(true);
  const [recargar, setRecargar] = useState(true);
  const [modalStockOpened, setModalStockOpened] = useState(false);
  const [stockSeleccionado, setStockSeleccionado] = useState(null);

  useEffect(() => {
    setRecargar(prev => !prev);
  }, [recargarExterno]);

  const [data, setData] = useState({
    meta: {},
    rows: [],
  });

  const fetchData = async ({ offset, limit, filtros = {}, order, orderBy } = {}) => {
    orderBy = orderBy || filtroOrder.ordenadoPor || 'id';
    order = order || filtroOrder.orden || 'ASC';

    construirURLParamsFiltros(navigate, offset || 1, limit || 50, filtros, order, orderBy);

    try {
      setLoading(true);
      const response = await getProductosKaeAlmacen(idAlmacen);
      const items = Array.isArray(response) ? response : [];
      setData({
        meta: {
          total: items.length,
          per_page: items.length,
          last_page: 1,
          has_more_pages: false,
        },
        rows: formatData(items),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const formatData = (data) => {
    return data.map((row) => ({
      id: row.id,
      stock_id: row.stocks?.[0]?.id,
      nombre: row.nombre,
      sku: row.sku,
      categoria: row.categoria?.nombre || "",
      descripcion: row.descripcion,
      precio_compra: row.precio_compra,
      precio_venta: row.precio_venta,
      cantidad: row.stocks?.[0]?.cantidad ?? 0,
      stock_minimo: row.stocks?.[0]?.stock_minimo ?? 0,
      stock_maximo: row.stocks?.[0]?.stock_maximo ?? 0,
      stockBajo: (row.stocks?.[0]?.cantidad ?? 0) < (row.stocks?.[0]?.stock_minimo ?? 0),
    }));
  };

  const CellTooltip = ({ value }) => (
    <Tooltip title={value || ""} arrow>
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: 200 }}>
        {value || ""}
      </span>
    </Tooltip>
  );

  const productosHeaders = [
    {
      id: "nombre",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.name'),
      overrideFunc: (data, row) => <CellTooltip value={row.nombre} />,
    },
    {
      id: "sku",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.sku'),
      overrideFunc: (data, row) => <CellTooltip value={row.sku} />,
    },
    {
      id: "categoria",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.category'),
      noObjeto: true,
      overrideFunc: (data, row) => <CellTooltip value={row.categoria} />,
    },
    {
      id: "descripcion",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.description'),
      overrideFunc: (data, row) => (
        <Tooltip title={row.descripcion || ""} arrow>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: 150 }}>
            {row.descripcion || ""}
          </span>
        </Tooltip>
      ),
    },
    {
      id: "precio_compra",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.purchasePrice'),
      overrideFunc: (data, row) => {
        const val = row.precio_compra ? `${row.precio_compra} €` : "";
        return <CellTooltip value={val} />;
      },
    },
    {
      id: "precio_venta",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.salePrice'),
      overrideFunc: (data, row) => {
        const val = row.precio_venta ? `${row.precio_venta} €` : "";
        return <CellTooltip value={val} />;
      },
    },
    {
      id: "cantidad",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.cuantity'),
      overrideFunc: (data, row) => {
        return data;
      },
    },
    {
      id: "stockBajo",
      numeric: false,
      disablePadding: false,
      label: t('almacenes.tooltips.stockBajo'),
      noObjeto: true,
      overrideFunc: (data, row) => (
        <div style={{ display: 'flex' }}>
          {data ?
            <Tooltip title={t('almacenes.tooltips.stockBajo')} arrow>
                <img src={Warning} style={{ margin:2}} width={15} alt="stock bajo" />
            </Tooltip>
          : null}
        </div>
      ),
    },
    {
      id: "acciones",
      numeric: false,
      disablePadding: false,
      label: "",
      noObjeto: true,
      overrideFunc: (data, row) => (
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {(user.rol === "ADMIN" || user.rol === "VENT" || user.rol === "TECH" || user.rol === "TECH_COOR") && (
            <Pencil
              size={20}
              className="tableIcon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setStockSeleccionado(row);
                setModalStockOpened(true);
              }}
            />
          )}
          {user.rol === "ADMIN" && (
            <img
              src={Delete}
              alt={t('actions.delete')}
              className="tableIcon"
              onClick={() => eliminarStock(row)}
            />
          )}
        </div>
      ),
    },
  ];

  const eliminarStock = (row) => {
    Swal.fire({
      title: t('almacenes.messages.confirmDeleteStock'),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      cancelButtonText: t('actions.cancel'),
      confirmButtonText: t('messages.yesDelete'),
    })
      .then((result) => {
        if (result.isConfirmed) {
          deleteStockKae(row.stock_id)
            .then((res) => {
              if (res.message === "STOCK_KAE_ELIMINADO") {
                toast.success(t('almacenes.messages.stockDeleted'));
                setRecargar(!recargar);
                if (setRecargarExterno) setRecargarExterno(prev => !prev);
              } else {
                toast.error(t('almacenes.messages.stockDeleteError'));
              }
            })
            .catch((e) => {
              toast.error(t('almacenes.messages.stockDeleteError'));
              console.log(e);
            });
        }
      })
      .catch((e) => {
        toast.error(t('messages.error'));
        console.log(e);
      });
  };

  return (
    <>
      <ModalEditarStockProducto
        opened={modalStockOpened}
        stock={stockSeleccionado}
        almacenId={idAlmacen}
        handleClose={() => {
          setModalStockOpened(false);
          setStockSeleccionado(null);
          setRecargar(!recargar);
          if (setRecargarExterno) setRecargarExterno(prev => !prev);
        }}
      />
      <div className="pageContent tableContainer">
        <div className="tableWrapper">
          <Box className="pageContent">
            <Box sx={{ mb: 2 }}>
              <DataTable
                data={data.rows}
                isLoading={loading}
                headers={productosHeaders}
                fetcher={fetchData}
                currentPage={data.current_page}
                meta={data.meta}
                totalItems={data.total}
                totalPages={data.totalPages}
                nextPage={data.next_page}
                mensaje={t('products.messages.noProducts')}
                recargar={recargar}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
};

export default withRouter(Productos);
